import React, { ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { Booking } from "@rentablo/api/apiv1";
import { MobileListHeader, MonthGroupedList } from "@components/MobileList";
import { BookingMobileRow } from "./BookingMobileRow";
import { investmentLogo } from "./investmentLogo";

const bookingDate = (booking: Booking) => booking.valueDate;
const bookingId = (booking: Booking) => booking.id;

interface Props {
  bookings: Booking[];
  logos: Record<string, string | null | undefined>;
  // All bookings in the range, not just the pages loaded so far.
  totalCount?: number;
  // Month headers fit a list sorted by date only.
  groupByMonth?: boolean;
  // An action for the whole list in the header line, e.g. Export.
  headerAction?: ReactNode;
  // At the end of the header line, shown even when the range has no bookings.
  settingsAction?: ReactNode;
  // Still the previous list while one for a new range or filter loads.
  isRefreshing?: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreFailed?: boolean;
  onLoadMore: () => void;
  // Marks the end once the list ran over more than one page.
  showEndOfList?: boolean;
  onRowClick?: (booking: Booking) => void;
}

export const BookingMobileList = ({
  bookings,
  logos,
  totalCount,
  groupByMonth = true,
  headerAction,
  settingsAction,
  isRefreshing = false,
  hasMore,
  isLoadingMore,
  loadMoreFailed = false,
  onLoadMore,
  showEndOfList = false,
  onRowClick,
}: Props) => {
  const { t } = useTranslation();
  const renderBooking = useCallback(
    (booking: Booking) => (
      <BookingMobileRow
        booking={booking}
        logo={investmentLogo(logos, booking.investment)}
        onRowClick={onRowClick}
      />
    ),
    [logos, onRowClick]
  );

  return (
    <Box
      data-testid="booking-mobile-list"
      aria-busy={isRefreshing}
      sx={{ opacity: isRefreshing ? 0.6 : 1 }}
    >
      <MobileListHeader
        count={
          totalCount !== undefined &&
          t("components.depotsStatistic.bookingsTable.mobile.count", {
            count: totalCount,
          })
        }
      >
        {bookings.length > 0 && headerAction}
        {settingsAction}
      </MobileListHeader>

      <MonthGroupedList
        items={bookings}
        getDate={bookingDate}
        getId={bookingId}
        renderItem={renderBooking}
        groupByMonth={groupByMonth}
        emptyText={t("components.depotsStatistic.bookingsTable.noItems")}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        loadMoreFailed={
          loadMoreFailed
            ? {
                message: t(
                  "components.depotsStatistic.bookingsTable.mobile.loadMoreFailed"
                ),
                retryLabel: t(
                  "components.depotsStatistic.bookingsTable.mobile.retry"
                ),
              }
            : undefined
        }
        onLoadMore={onLoadMore}
        loadingMoreText={t(
          "components.depotsStatistic.bookingsTable.mobile.loadingMore"
        )}
        endText={
          showEndOfList &&
          t("components.depotsStatistic.bookingsTable.mobile.allLoaded")
        }
        loadingTestId="booking-mobile-list-loading"
      />
    </Box>
  );
};
